import { useCallback, useMemo, useState } from 'react';

import { type Item, type PeriodDetail, useShiftFocus } from '@atlassian/eoc-schedule-common';
import { useSpotlightTrigger } from '@atlassian/eoc-spotlight-store';

import { SPOTLIGHT_TOURS } from '../../common/constants';
import { type ScheduleEntity } from '../../services/endpoints/list/types';
import { useShiftRelatedModalOpen } from '../page-store/helpers';
import { useStore as useShiftStore } from '../shift-store';

type UseShiftParams = {
	schedule: ScheduleEntity;
	items: Item[][];
	now: number;
};

const focus = (refs: (null | undefined | React.RefObject<HTMLElement | null>)[]) => {
	while (Array.isArray(refs) && refs.length > 0) {
		const ref = refs.shift();

		if (ref && ref.current && document.body.contains(ref.current)) {
			ref.current.focus();
			break;
		}
	}
};

const useShift = ({ schedule, items, now }: UseShiftParams) => {
	const [isModalOpen] = useShiftRelatedModalOpen(schedule);
	const [isSpotlightTriggered, { trigger }] = useSpotlightTrigger(SPOTLIGHT_TOURS.shiftTile);
	const [{ cards }, { click, select, clear }] = useShiftStore();
	const { tab, tabbed, focusRefs, onContainerKeyDown } = useShiftFocus(items, now);

	const [activeSpotlight, setActiveSpotlight] = useState<string>();

	const activeShift = useMemo(() => {
		return cards.length >= 1 ? cards[cards.length - 1].id : undefined;
	}, [cards]);

	const selectedShifts = useMemo(() => {
		return cards.reduce(
			(acc, card) => {
				acc[card.id] = true;
				return acc;
			},
			{} as Record<string, boolean>,
		);
	}, [cards]);

	const makeOnHover = useCallback<(card: PeriodDetail) => React.MouseEventHandler<HTMLDivElement>>(
		(card) => () => {
			// if spotlight tour hasn't rendered before, then it is the first and only valid target
			if (!isSpotlightTriggered) {
				setActiveSpotlight(card.id);
				trigger(SPOTLIGHT_TOURS.shiftTile);
			}
		},
		[isSpotlightTriggered, trigger],
	);

	const makeOnClick = useCallback<
		(
			card: PeriodDetail,
		) => (
			e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement, MouseEvent>,
		) => void
	>(
		(card) => (e) => {
			if (e.ctrlKey || e.metaKey) {
				if (select(card)) {
					tab(card.id);
				}
			} else {
				if (click(card)) {
					tab(card.id);
				}
			}
		},
		[click, select, tab],
	);

	// FIX: Find a workaround for this.
	// Problem is that updates occur in batches, so when we click on to some other shift,
	// first we get the click action, then we get the clear action. But as the clear action
	// is the final one, it overrides the click action.
	const makeOnClose = useCallback<
		(card: PeriodDetail) => (event: Event | React.MouseEvent | React.KeyboardEvent) => void
	>(
		(card) => (e) => {
			if (e.target instanceof HTMLElement) {
				const target = e.target.closest('[data-shift-id]');

				if (target && Object.values(focusRefs).some((ref) => ref.current === target)) {
					return;
				}
			}

			if (isModalOpen) {
				return;
			}

			clear();

			window.setTimeout(
				(refs: (null | undefined | React.RefObject<HTMLElement | null>)[]) => {
					focus(refs);
				},
				0,
				[focusRefs[card.id], { current: document.getElementById(schedule.id) }],
			);
		},
		[clear, focusRefs, isModalOpen, schedule.id],
	);

	const makeOnCross = useCallback<(card: PeriodDetail) => () => void>(
		(card) => () => {
			clear();

			window.setTimeout(
				(refs: (null | undefined | React.RefObject<HTMLElement | null>)[]) => {
					focus(refs);
				},
				0,
				[focusRefs[card.id], { current: document.getElementById(schedule.id) }],
			);
		},
		[clear, focusRefs, schedule.id],
	);

	return {
		tabbed,
		focusRefs,
		onContainerKeyDown,

		makeOnClick,
		makeOnHover,
		/** @description On implicit close, on other item click, on esc. etc. */
		makeOnClose,
		/** @description On explicit close, cross press etc. */
		makeOnCross,

		activeSpotlight,
		activeShift,
		selectedShifts,
	};
};

export { useShift };
